import React, { useEffect, useMemo, useState } from 'react';
import { useStreamAuth } from '../hooks/useStreamAuth';
import { dspTokenService } from '../services/auth/DSPTokenService';
import subscriptionService from '../services/SubscriptionService';
import { safeUserMessage } from '../utils/errorMessages';
import { formatMoney } from '../utils/money';

const ENTITLED_STATUSES = new Set(['active', 'trialing', 'in_grace_period', 'grace_period']);

const DASHBOARD_TABS = [
  { id: 'subscription', label: 'Subscription' },
  { id: 'account', label: 'Account' },
];

// Informational sections folded into the Account tab (formerly the standalone
// Settings page). On the web each renders body copy plus a direct link to the
// BKFC site (the QR-code variant is for the TV / mobile apps).
const INFO_SECTIONS = [
  {
    id: 'about-us',
    label: 'About',
    heading: 'About Us',
    body: 'Learn more about Bare Knuckle Fighting Championship — our story, our athletes, and the sport.',
    cta: 'About BKFC',
    url: 'https://bkfc.com/about',
  },
  {
    id: 'privacy-policy',
    label: 'Privacy',
    heading: 'Privacy Policy',
    body: 'Read how BKFC collects, uses, and protects your personal information.',
    cta: 'View Privacy Policy',
    url: 'https://bkfc.com/privacy-policy',
  },
  {
    id: 'terms-of-use',
    label: 'Terms',
    heading: 'Terms of Use',
    body: 'Review the terms and conditions that govern your use of BKFC.',
    cta: 'View Terms of Use',
    url: 'https://bkfc.com/terms-of-use',
  },
  {
    id: 'connect',
    label: 'Connect',
    heading: 'Connect With Us',
    body: 'Have a question or need a hand? Get in touch with the BKFC team.',
    cta: 'Contact Us',
    url: 'https://bkfc.com/about/contact',
  },
];

const toLowerStatus = value => (typeof value === 'string' ? value.trim().toLowerCase() : '');

// A subscription record existing in the array does not mean the user is
// subscribed — the bridge API now returns lapsed subscriptions as real
// records with status "expired" (bkfc-cms#295) instead of omitting them, so
// only an entitled record counts; there is no "closest match" fallback.
const findEntitledSubscription = subscriptions =>
  subscriptions.find(subscription => ENTITLED_STATUSES.has(toLowerStatus(subscription?.status))) ||
  null;

// The subscriptions API sends Unix timestamps in epoch seconds, but the Date
// constructor reads numbers as milliseconds. Anything below 1e12 (Sep 2001 in
// ms, year 33658 in s) can only be seconds, so scale it; ISO strings pass through.
const toDateValue = value => {
  const numeric = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof numeric === 'number' && Number.isFinite(numeric)) {
    return numeric < 1e12 ? numeric * 1000 : numeric;
  }
  return value;
};

const formatDateLabel = value => {
  if (!value) {
    return '—';
  }

  const date = new Date(toDateValue(value));
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getSubscriptionDate = subscription => {
  const { next_billing_at: nextBilling, period_end: periodEnd } = subscription?.billing_cycle || {};
  // A cancelled-but-active plan has no next charge — its period_end is the date
  // access lapses, so read that first when `cancel_at_period_end` is set.
  const preferred = subscription?.cancel_at_period_end ? periodEnd : nextBilling;
  return preferred || nextBilling || periodEnd || subscription?.purchased_at || null;
};

const normalizeUserSubscriptions = payload => {
  if (Array.isArray(payload?.subscriptions)) {
    return payload.subscriptions;
  }

  if (payload?.subscription && typeof payload.subscription === 'object') {
    return [payload.subscription];
  }

  if (Array.isArray(payload?.products?.svod)) {
    return payload.products.svod;
  }

  return [];
};

const normalizeHistoryRows = historyPayload => {
  const subscriptions = Array.isArray(historyPayload?.subscriptions)
    ? historyPayload.subscriptions
    : [];
  const purchases = Array.isArray(historyPayload?.purchases) ? historyPayload.purchases : [];

  const mappedSubscriptions = subscriptions.map(item => ({
    id: item.id || item._id || `${item.product_id || 'sub'}-${item.status || 'unknown'}`,
    type: 'Subscription',
    name: item.name || 'Subscription',
    status: item.status || 'unknown',
    amount: item?.pricing?.amount || item?.price || '',
    currency: item?.pricing?.currency || 'USD',
    date: getSubscriptionDate(item),
  }));

  const mappedPurchases = purchases.map(item => ({
    id: item.id || item._id || `${item.product_id || 'purchase'}-${item.purchased_at || 'unknown'}`,
    type: 'Purchase',
    name: item.name || item.title || 'Purchase',
    status: item.status || 'completed',
    amount: item?.pricing?.amount || item?.price || '',
    currency: item?.pricing?.currency || 'USD',
    date: item.purchased_at || item.created_at || null,
  }));

  return [...mappedSubscriptions, ...mappedPurchases].sort((a, b) => {
    const aTime = a.date ? new Date(toDateValue(a.date)).getTime() : 0;
    const bTime = b.date ? new Date(toDateValue(b.date)).getTime() : 0;
    return bTime - aTime;
  });
};

const StreamingDashboard = () => {
  const { user, logout } = useStreamAuth();
  const [activeTab, setActiveTab] = useState('subscription');
  const [activeInfoSection, setActiveInfoSection] = useState('about-us');
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [subscriptionActionMsg, setSubscriptionActionMsg] = useState('');
  const [subscriptionActionErr, setSubscriptionActionErr] = useState('');
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [isCancelingPlan, setIsCancelingPlan] = useState(false);
  const [deleteState, setDeleteState] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [acknowledgeDelete, setAcknowledgeDelete] = useState(false);

  const currentSubscription = useMemo(
    () => findEntitledSubscription(activeSubscriptions),
    [activeSubscriptions],
  );

  // Backend reports a cancelled-but-in-period plan as `active` (it still grants
  // access); `cancel_at_period_end` is the only signal that it won't renew.
  const cancelAtPeriodEnd = Boolean(currentSubscription?.cancel_at_period_end);
  const accessEndsLabel = formatDateLabel(getSubscriptionDate(currentSubscription));

  const loadSubscriptionTabData = async () => {
    setSubscriptionLoading(true);
    setSubscriptionError('');

    try {
      const [productsPayload, historyPayload, catalogPayload] = await Promise.all([
        subscriptionService.getUserProducts(),
        subscriptionService.getPaymentHistory(),
        subscriptionService.getSubscriptionCatalog(),
      ]);

      const subscriptions = normalizeUserSubscriptions(productsPayload);
      const plans = Array.isArray(catalogPayload?.products)
        ? catalogPayload.products.filter(plan => toLowerStatus(plan?.product_type) === 'svod')
        : [];

      setActiveSubscriptions(subscriptions);
      setAvailablePlans(plans);
      setPaymentHistory(normalizeHistoryRows(historyPayload));

      if (!selectedPlanId) {
        const currentActive = findEntitledSubscription(subscriptions);
        const currentProductId = currentActive?.product_id || currentActive?._id || '';
        const recommendedPlan =
          plans.find(plan => plan._id && plan._id !== currentProductId) || plans[0];
        setSelectedPlanId(recommendedPlan?._id || '');
      }
      return subscriptions;
    } catch (error) {
      setSubscriptionError(
        safeUserMessage(error, 'Unable to load subscription details. Please try again.'),
      );
    } finally {
      setSubscriptionLoading(false);
    }
  };

  useEffect(() => {
    loadSubscriptionTabData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePlan = async () => {
    if (!selectedPlanId) {
      setSubscriptionActionErr('Select a plan first.');
      setSubscriptionActionMsg('');
      return;
    }

    if (
      selectedPlanId &&
      (selectedPlanId === currentSubscription?.product_id ||
        selectedPlanId === currentSubscription?._id)
    ) {
      setSubscriptionActionErr('You are already on that plan.');
      setSubscriptionActionMsg('');
      return;
    }

    setIsChangingPlan(true);
    setSubscriptionActionErr('');
    setSubscriptionActionMsg('');

    try {
      await subscriptionService.changePlan({
        productId: selectedPlanId,
        immediate: false,
      });
      setSubscriptionActionMsg('Plan update submitted successfully.');
      await loadSubscriptionTabData();
    } catch (error) {
      setSubscriptionActionErr(error.message || 'Unable to change plan right now.');
    } finally {
      setIsChangingPlan(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!currentSubscription) {
      setSubscriptionActionErr('No active subscription found to cancel.');
      setSubscriptionActionMsg('');
      return;
    }

    setIsCancelingPlan(true);
    setSubscriptionActionErr('');
    setSubscriptionActionMsg('');

    try {
      const result = await subscriptionService.cancelSubscription({
        subscriptionId: currentSubscription?.id || currentSubscription?._id || null,
        reason: cancelReason.trim() || null,
      });
      setCancelReason('');
      // The reloaded record's period end is the access-until date; the cancel
      // response's `access_until` covers a reload that fails or lags.
      const reloaded = findEntitledSubscription((await loadSubscriptionTabData()) || []);
      const accessUntil =
        (reloaded?.cancel_at_period_end && getSubscriptionDate(reloaded)) || result?.access_until;
      setSubscriptionActionMsg(
        accessUntil
          ? `Your subscription is cancelled. You'll keep access until ${formatDateLabel(accessUntil)}.`
          : "Your subscription is cancelled. You'll keep access until the end of your current billing period.",
      );
    } catch (error) {
      setSubscriptionActionErr(
        safeUserMessage(error, 'Unable to cancel subscription right now. Please try again.'),
      );
    } finally {
      setIsCancelingPlan(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteState('deleting');
    setErrorMsg('');

    try {
      const clientToken = await dspTokenService.getClientToken();
      const dotstudioToken = await dspTokenService.getDotstudioToken();

      const response = await fetch(`${dspTokenService.baseUrl}/users/account/purge`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(dotstudioToken ? { 'x-access-token': dotstudioToken } : {}),
          ...(clientToken ? { 'x-client-token': clientToken } : {}),
        },
      });

      if (response.status === 400) {
        setErrorMsg(
          'Cannot delete account while you have an active subscription. Please cancel your subscription first.',
        );
        setDeleteState('idle');
        return;
      }

      if (response.status === 409) {
        setDeleteState('scheduled');
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to delete account (${response.status})`);
      }

      setDeleteState('scheduled');
    } catch (err) {
      console.error('StreamingDashboard: Error deleting account:', err);
      setErrorMsg('Something went wrong. Please try again.');
      setDeleteState('idle');
    }
  };

  return (
    <div className="min-h-screen bg-primary flex flex-col">
      <main className="flex-1 px-6 py-10">
        <div className="w-full max-w-4xl mx-auto">
          <h1 className="text-3xl font-display uppercase tracking-wide text-white mb-8 text-center">
            My Account
          </h1>

          <div className="flex items-center justify-center gap-2 mb-8">
            {DASHBOARD_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-md text-sm font-display uppercase tracking-wide transition-colors ${
                  activeTab === tab.id
                    ? 'bg-accent text-primary'
                    : 'bg-secondary/60 text-white border border-white/10 hover:border-white/30'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="bg-secondary/60 rounded-lg p-6 mb-6 border border-white/10">
            <label className="block text-sm text-white/50 font-body mb-1">Email</label>
            <p className="text-white font-body text-lg">{user?.email || '—'}</p>
          </div>

          {activeTab === 'subscription' && (
            <div className="space-y-6">
              <div className="bg-secondary/60 rounded-lg p-6 border border-white/10">
                <h2 className="text-white font-display uppercase tracking-wide mb-3">
                  Current Subscription
                </h2>
                {subscriptionLoading ? (
                  <p className="text-white/60">Loading subscription details...</p>
                ) : subscriptionError ? (
                  <p className="text-red-300">{subscriptionError}</p>
                ) : currentSubscription ? (
                  <div className="space-y-2 text-sm">
                    <p className="text-white">
                      <span className="text-white/60">Plan:</span> {currentSubscription.name || '—'}
                    </p>
                    <p className="text-white">
                      <span className="text-white/60">Status:</span>{' '}
                      {cancelAtPeriodEnd
                        ? `Cancelled — access until ${accessEndsLabel}`
                        : currentSubscription.status || '—'}
                    </p>
                    <p className="text-white">
                      <span className="text-white/60">
                        {cancelAtPeriodEnd ? 'Access Until:' : 'Next Billing:'}
                      </span>{' '}
                      {accessEndsLabel}
                    </p>
                    <p className="text-white">
                      <span className="text-white/60">Amount:</span>{' '}
                      {formatMoney(
                        currentSubscription?.pricing?.amount || '',
                        currentSubscription?.pricing?.currency || 'USD',
                      )}
                    </p>
                  </div>
                ) : (
                  <p className="text-white/60">No active subscription found.</p>
                )}
              </div>

              {/* Change Plan feature temporarily disabled
              <div className="bg-secondary/60 rounded-lg p-6 border border-white/10">
                <h2 className="text-white font-display uppercase tracking-wide mb-3">
                  Change Plan
                </h2>
                <div className="flex flex-col md:flex-row gap-3">
                  <label htmlFor="change-plan" className="sr-only">
                    Select plan
                  </label>
                  <select
                    id="change-plan"
                    name="planId"
                    value={selectedPlanId}
                    onChange={event => setSelectedPlanId(event.target.value)}
                    className="flex-1 rounded-lg bg-primary border border-white/20 px-3 py-2 text-white"
                  >
                    <option value="">Select plan</option>
                    {availablePlans.map(plan => (
                      <option key={plan._id} value={plan._id}>
                        {plan.name} ({formatMoney(plan.price, plan.currency || 'USD')})
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleChangePlan}
                    disabled={isChangingPlan || subscriptionLoading}
                    className="px-4 py-2 rounded-lg bg-accent text-primary font-display uppercase tracking-wide disabled:opacity-60"
                  >
                    {isChangingPlan ? 'Updating...' : 'Change Plan'}
                  </button>
                </div>
                <p className="text-white/50 text-xs mt-2">
                  Plan changes are scheduled at end of billing cycle unless configured otherwise.
                </p>
              </div>
              */}

              <div className="bg-secondary/60 rounded-lg p-6 border border-white/10">
                <h2 className="text-white font-display uppercase tracking-wide mb-3">
                  {cancelAtPeriodEnd ? 'Subscription Cancelled' : 'Cancel Subscription'}
                </h2>
                {cancelAtPeriodEnd ? (
                  <p className="text-white/70 text-sm">
                    Your plan won&apos;t renew. You keep full access until {accessEndsLabel}.
                  </p>
                ) : (
                  <>
                    <textarea
                      id="cancel-reason"
                      name="cancelReason"
                      aria-label="Cancellation reason"
                      value={cancelReason}
                      onChange={event => setCancelReason(event.target.value)}
                      rows={2}
                      placeholder="Optional cancellation reason"
                      className="w-full rounded-lg bg-primary border border-white/20 px-3 py-2 text-white mb-3"
                    />
                    <button
                      onClick={handleCancelSubscription}
                      disabled={isCancelingPlan || subscriptionLoading || !currentSubscription}
                      className="px-4 py-2 rounded-lg bg-red-600 text-white font-display uppercase tracking-wide disabled:opacity-60"
                    >
                      {isCancelingPlan ? 'Canceling...' : 'Cancel Subscription'}
                    </button>
                  </>
                )}
              </div>

              {(subscriptionActionErr || subscriptionActionMsg) && (
                <div
                  className={`rounded-lg p-4 border ${
                    subscriptionActionErr
                      ? 'bg-red-900/20 border-red-500/30 text-red-300'
                      : 'bg-green-900/20 border-green-500/30 text-green-300'
                  }`}
                >
                  {subscriptionActionErr || subscriptionActionMsg}
                </div>
              )}

              {/* Payment History feature temporarily disabled
              <div className="bg-secondary/60 rounded-lg p-6 border border-white/10">
                <h2 className="text-white font-display uppercase tracking-wide mb-3">
                  Payment History
                </h2>
                {subscriptionLoading ? (
                  <p className="text-white/60">Loading payment history...</p>
                ) : paymentHistory.length === 0 ? (
                  <p className="text-white/60">No payment history available yet.</p>
                ) : (
                  <div className="space-y-2">
                    {paymentHistory.map(item => (
                      <div
                        key={item.id}
                        className="grid grid-cols-1 md:grid-cols-4 gap-2 bg-primary/40 rounded-lg px-3 py-2 text-sm"
                      >
                        <p className="text-white">{item.name}</p>
                        <p className="text-white/70">{item.type}</p>
                        <p className="text-white/70">{formatDateLabel(item.date)}</p>
                        <p className="text-white/70">
                          {formatMoney(item.amount, item.currency)}{' '}
                          {item.status ? `• ${item.status}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              */}
            </div>
          )}

          {activeTab === 'account' &&
            (deleteState === 'scheduled' ? (
              <div className="bg-red-900/30 border border-red-500/40 rounded-lg p-6 text-center">
                <p className="text-red-300 font-body mb-2">
                  Your account is scheduled for deletion and will be permanently removed in 30 days.
                </p>
                <button
                  onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                  className="mt-4 px-6 py-2 font-display uppercase tracking-wide text-white border border-white/30 hover:border-white/60 rounded transition-colors"
                >
                  Log Out
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Info sections (About / Privacy / Terms / Connect) */}
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {INFO_SECTIONS.map(section => (
                    <button
                      key={section.id}
                      onClick={() => setActiveInfoSection(section.id)}
                      className={`px-4 py-2 rounded-md text-sm font-display uppercase tracking-wide transition-colors ${
                        activeInfoSection === section.id
                          ? 'bg-accent text-primary'
                          : 'bg-secondary/60 text-white border border-white/10 hover:border-white/30'
                      }`}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>

                {(() => {
                  const info =
                    INFO_SECTIONS.find(section => section.id === activeInfoSection) ||
                    INFO_SECTIONS[0];
                  return (
                    <div className="bg-secondary/60 rounded-lg p-6 border border-white/10">
                      <h2 className="text-white font-display uppercase tracking-wide mb-3">
                        {info.heading}
                      </h2>
                      <p className="text-white/70 text-sm mb-5 max-w-2xl">{info.body}</p>
                      <a
                        href={info.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent text-primary font-display uppercase tracking-wide text-sm transition-opacity hover:opacity-90"
                      >
                        {info.cta}
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M14 5h5v5m0-5L10 14m-1 7H6a2 2 0 01-2-2V6a2 2 0 012-2h3"
                          />
                        </svg>
                      </a>
                    </div>
                  );
                })()}

                {/* Delete account danger zone */}
                <div className="border-t border-white/10 pt-6">
                  {!showConfirm ? (
                    <button
                      onClick={() => setShowConfirm(true)}
                      className="w-full text-red-400 hover:text-red-300 font-body text-sm transition-colors"
                    >
                      Delete My Account
                    </button>
                  ) : (
                    <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
                      <h3 className="text-white font-display uppercase tracking-wide mb-3">
                        Delete Account
                      </h3>
                      <div className="space-y-2 text-white/80 font-body text-sm mb-4">
                        <p>
                          Disabling your account will delete all user data, including your saved and
                          watched titles.
                        </p>
                        <p>You have 30 days to reactivate it.</p>
                        <p>
                          To reactivate your account, contact us at{' '}
                          <a
                            href="https://bkfc.com/contact"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent break-all"
                          >
                            bkfc.com/contact
                          </a>{' '}
                          within 30 days of the deletion request.
                        </p>
                        <p>
                          After 30 days, deletion is permanent — your account will be permanently
                          disabled and cannot be recovered.
                        </p>
                      </div>

                      <label className="flex items-start gap-3 mb-4 cursor-pointer">
                        <input
                          id="acknowledge-delete"
                          name="acknowledgeDelete"
                          type="checkbox"
                          checked={acknowledgeDelete}
                          onChange={event => setAcknowledgeDelete(event.target.checked)}
                          className="mt-0.5 h-4 w-4 flex-shrink-0 accent-red-600"
                        />
                        <span className="text-white/80 font-body text-sm">
                          I understand that this will permanently delete my account and all of my
                          data after 30 days.
                        </span>
                      </label>

                      {errorMsg && (
                        <p className="text-red-400 font-body text-sm mb-4">{errorMsg}</p>
                      )}

                      <div className="flex gap-3">
                        <button
                          onClick={() => {
                            setShowConfirm(false);
                            setErrorMsg('');
                            setAcknowledgeDelete(false);
                          }}
                          className="flex-1 px-4 py-2 font-display uppercase tracking-wide text-white border border-white/30 hover:border-white/60 rounded transition-colors text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteAccount}
                          disabled={deleteState === 'deleting' || !acknowledgeDelete}
                          className="flex-1 px-4 py-2 font-display uppercase tracking-wide text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors text-sm"
                        >
                          {deleteState === 'deleting' ? 'Deleting...' : 'Confirm Delete'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </main>
    </div>
  );
};

export default StreamingDashboard;
